import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QuizLayout } from '../components/QuizLayout';
import { useQuizStore } from '../store/quizStore';
import { postJson } from '../lib/api';
import { track } from '../lib/analytics';
import { isValidEmail, isValidPhone } from '../lib/validators';

interface LeadResp {
  ok: boolean;
  leadId?: string;
  error?: string;
}

export function LeadCapture() {
  const navigate = useNavigate();
  const sessionId = useQuizStore((s) => s.sessionId);
  const setLead = useQuizStore((s) => s.setLead);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<{ name?: string; email?: string; phone?: string; submit?: string }>({});
  const [submitting, setSubmitting] = useState(false);

  const validate = (): boolean => {
    const e: typeof errors = {};
    if (!name.trim()) e.name = 'Please share your name';
    // Block email-shaped names — otherwise the user's email becomes their
    // "name" on the pricing page + dashboard, which leaks PII into greetings
    // and the avatar pill. (Real human names never contain '@'.)
    else if (name.includes('@')) e.name = 'Please enter your name, not your email';
    if (!email.trim()) e.email = 'Please share your email';
    else if (!isValidEmail(email)) e.email = 'Hmm, that email doesn’t look right';
    if (phone.trim() && !isValidPhone(phone)) e.phone = 'Phone number doesn’t look right (optional — feel free to skip)';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    if (!sessionId) {
      // Should be impossible since the URL guard requires a session, but
      // guard anyway and bounce to start.
      navigate('/');
      return;
    }
    setSubmitting(true);
    setErrors({});
    const resp = await postJson<LeadResp>('/api/leads', {
      client_session_id: sessionId,
      email,
      name,
      phone: phone.trim() || null,
    });
    if (resp?.ok && resp.leadId) {
      setLead(resp.leadId, { email, name, phone: phone.trim() || null });
      track('lead_captured', { hasPhone: !!phone.trim() });
      navigate('/building-report');
      return;
    }
    if (resp == null) {
      // Queued for retry. Optimistically advance — building-report flow will
      // try again on mount.
      setLead('queued', { email, name, phone: phone.trim() || null });
      track('lead_captured', { hasPhone: !!phone.trim(), queued: true });
      navigate('/building-report');
      return;
    }
    setSubmitting(false);
    setErrors({ submit: resp.error ?? 'Something went wrong. Please try again.' });
  };

  const inputClass =
    'w-full rounded-2xl border bg-white px-5 py-4 text-base outline-none focus:border-brand focus:ring-2 focus:ring-brand-100';

  return (
    <QuizLayout>
      <p className="eyebrow mb-3">Last step</p>
      <h1 className="text-display-md text-ink mb-4">
        One last thing — where should we send your report?
      </h1>
      <p className="text-ink-soft text-base mb-7">
        We'll email you the personalized report and use this only to make sure
        you get it. No spam.
      </p>

      <form onSubmit={handleSubmit} noValidate>
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-semibold text-ink mb-1.5" htmlFor="name">
              Name
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              className={`${inputClass} ${errors.name ? 'border-err' : 'border-ink-light/40'}`}
              required
            />
            {errors.name && <p className="text-sm text-err mt-1.5">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold text-ink mb-1.5" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              inputMode="email"
              className={`${inputClass} ${errors.email ? 'border-err' : 'border-ink-light/40'}`}
              required
            />
            {errors.email && <p className="text-sm text-err mt-1.5">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold text-ink mb-1.5" htmlFor="phone">
              Phone <span className="text-ink-soft font-normal">(optional)</span>
            </label>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="tel"
              inputMode="tel"
              className={`${inputClass} ${errors.phone ? 'border-err' : 'border-ink-light/40'}`}
            />
            {errors.phone && <p className="text-sm text-err mt-1.5">{errors.phone}</p>}
          </div>
        </div>

        {errors.submit && <p className="text-sm text-err mb-3">{errors.submit}</p>}

        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? 'Building your report…' : 'Continue →'}
        </button>
        <p className="text-xs text-ink-soft text-center mt-5">
          By continuing you agree to our terms. We never share your information.
        </p>
      </form>
    </QuizLayout>
  );
}
